/**
 * features/applications/PublicApplicationForm.tsx
 * ------------------------------------------------------------------
 * Public form parents fill in to apply for admission to LKC. Mounted
 * at `/apply` (or `?form=apply`) by App.tsx — BYPASSES the auth gate.
 *
 * Submits via the `submitApplication` Cloud Function which:
 *   - validates server-side (mirroring our client validation)
 *   - rate-limits per IP
 *   - assigns a reference number (APP-YYYY-NNNN)
 *   - sends the parent an acknowledgement email
 *   - writes the doc to reportApplications
 *
 * Anti-abuse: a hidden honeypot field that real users never see; if
 * filled the submission is silently dropped. reCAPTCHA v3 hook is
 * stubbed (see TODO) for post-approval enablement.
 *
 * Validation: phone numbers must use country codes; Botswana mobiles
 * must start with 71-78 (8 digits); Botswana landlines must start
 * with 2-6 (7 digits). All rules in src/lib/validation.ts.
 *
 * The form reads `reportSchoolSettings/admissionWindow` (public-read)
 * to know which forms are currently accepting applications and shows
 * a closed-state message when applications are paused.
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  User,
  Users,
  Stethoscope,
  ClipboardList,
  ScrollText,
  CheckCircle2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Star,
  Loader2,
  Send,
  Copy,
  Calendar,
  Phone,
  Mail,
  MessageCircle,
  Globe,
  Briefcase,
  Building2,
  MapPin,
  Shield,
  Home,
  BookOpen,
  Wallet,
  GraduationCap,
  UserPlus,
  IdCard,
} from 'lucide-react';
import { doc, getDoc, collection, addDoc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { SCHOOL } from '@/lib/school.config';
import {
  submissionShapeProblem, withSubmitTimeout, explainSubmitError, SUBMISSION_LIMITS,
} from '@/lib/applicationSubmission';
import {
  DEFAULT_CONDITIONS, resolveAdmissionFormText,
} from '@/lib/admissionFormDefaults';
import SchoolLogo from '@/components/ui/SchoolLogo';
import {
  reloadToLatest, subscribeSwUpdate, hasSwTakenOver, isSwUpdateAvailable,
} from '@/lib/swUpdate';
import {
  validateEmail,
  validatePhone,
  validateName,
  validateDateOfBirth,
  validateIdNumber,
  validateOccupation,
} from '@/lib/validation';
import type { AdmissionFormText } from '@/types';
import type {
  Guardian,
  GuardianRole,
  AdmissionWindowSettings,
  PreviousSchoolEntry,
} from '@/types';

/* -------------------- constants & types -------------------- */

const FORM_LEVELS = [
  { id: '1', label: 'Form 1' },
  { id: '2', label: 'Form 2' },
  { id: '3', label: 'Form 3' },
  { id: '4', label: 'Form 4' },
  { id: '5', label: 'Form 5' },
  { id: 'AS', label: 'AS Level' },
  { id: 'A2', label: 'A2 Level' },
];

const GUARDIAN_ROLE_LABELS: Record<GuardianRole, string> = {
  father: 'Father',
  mother: 'Mother',
  guardian: 'Guardian',
  stepfather: 'Stepfather',
  stepmother: 'Stepmother',
  grandparent: 'Grandparent',
  other: 'Other',
};

type TabId =
  | 'student'
  | 'guardians'
  | 'education'
  | 'medical'
  | 'additional'
  | 'review'
  /* LAST, and it carries the submit button. The school asked that the
     Conditions of Admission be read and acknowledged immediately before
     submitting, not buried on an earlier page somebody clicked past. */
  | 'terms';
const TABS: { id: TabId; label: string; icon: typeof User }[] = [
  { id: 'student', label: 'Student details', icon: User },
  { id: 'guardians', label: 'Guardians', icon: Users },
  { id: 'education', label: 'Previous schools', icon: GraduationCap },
  { id: 'medical', label: 'Medical & emergency', icon: Stethoscope },
  { id: 'additional', label: 'Additional', icon: UserPlus },
  { id: 'review', label: 'Review', icon: ClipboardList },
  { id: 'terms', label: 'Terms & submit', icon: ScrollText },
];

interface PersonalState {
  firstName: string;
  surname: string;
  gender: 'M' | 'F' | '';
  dateOfBirth: string;
  placeOfBirth: string;
  nationality: string;
  homeLanguage: string;
  religion: string;
  idPassportNumber: string;
  studentEmail: string;
  studentPhone: string;
  address: string;
  applyingForForm: string;
  applyingForYear: string;
  boardingStatus: 'day' | 'boarding';
  notes: string;
}

interface MedicalState {
  doctorName: string;
  doctorPhone: string;
  medicalAidProvider: string;
  medicalAidNumber: string;
  allergies: string;
  chronicConditions: string;
  medications: string;
  bloodType: string;
  hasMedicalCondition: boolean;
  medicalConditionDetails: string;
  emergencyTreatmentConsent: boolean;
  emergencyTreatmentNoConsentAction: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactEmail: string;
}

/** Previous-schooling wizard state (paper §4). */
interface EducationState {
  last: PreviousSchoolEntry;
  others: PreviousSchoolEntry[];
}

/** Siblings-at-school wizard state (paper §6). */
interface SiblingState {
  hasSiblingAtSchool: boolean;
  sibling1: { names: string; surname: string };
  sibling2: { names: string; surname: string };
  siblingParentNames: string;
  siblingParentEmail: string;
}

const EMPTY_PREV_SCHOOL = (): PreviousSchoolEntry => ({
  name: '',
  year: '',
  formClass: '',
  contact: '',
});

const EMPTY_GUARDIAN = (isPrimary: boolean): Guardian => ({
  id: `g_new_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  role: 'mother',
  fullName: '',
  idNumber: '',
  nationality: '',
  occupation: '',
  employer: '',
  phone: '',
  workPhone: '',
  homeTel: '',
  whatsapp: '',
  email: '',
  address: '',
  responsibleForFees: false,
  emergencyContactName: '',
  emergencyContactPhone: '',
  isPrimaryContact: isPrimary,
  consentToSend: false,
  consentToWhatsapp: false,
});

/* -------------------- field helpers -------------------- */

function FieldLabel({
  children,
  required,
  icon: Icon,
}: {
  children: React.ReactNode;
  required?: boolean;
  icon?: typeof User;
}) {
  return (
    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
      {Icon && <Icon size={14} className="text-gray-400" />}
      {children}
      {required && <span className="text-red-500">*</span>}
    </label>
  );
}

function ErrorText({ msg }: { msg?: string | null }) {
  if (!msg) return null;
  return (
    <p className="mt-1 text-xs text-red-600 flex items-start gap-1">
      <AlertCircle size={12} className="mt-0.5 shrink-0" />
      <span>{msg}</span>
    </p>
  );
}

function inputClass(hasError?: boolean) {
  return [
    'w-full px-3 py-2.5 rounded-lg border-2',
    hasError
      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
      : 'border-gray-200 focus:border-[#2d5295] focus:ring-[#2d5295]/10',
    'focus:outline-none focus:ring-2 transition-colors text-gray-900 bg-white',
  ].join(' ');
}

/* -------------------- main component -------------------- */

export default function PublicApplicationForm() {
  const [tab, setTab] = useState<TabId>('student');
  const [maxStep, setMaxStep] = useState(0);

  /* OPEN ON THE CURRENT FORM, NOT WHATEVER THIS BROWSER KEPT.
   *
   * Staff get a "new version" banner; this page never had one, because it
   * renders before the sign-in gate that mounts it. So a parent whose
   * browser cached the form stayed on that copy indefinitely — and after
   * 5 Sep 2026 that copy could not produce an application the database
   * would accept. They filled in every step and were refused at the end,
   * every time.
   *
   * On opening, ask for the latest worker at once. If one takes over
   * BEFORE the parent has typed anything, reload onto it: nothing is lost
   * and they are on the form the rules expect. Once they have started,
   * never reload — twenty minutes of a child's details is not ours to
   * throw away, and the copy they are on is already the one that
   * explains any refusal by name. */
  useEffect(() => {
    let touched = false;
    const markTouched = () => { touched = true; };
    window.addEventListener('input', markTouched, { capture: true, once: true });
    window.addEventListener('change', markTouched, { capture: true, once: true });

    const unsub = subscribeSwUpdate(() => {
      if (!touched) reloadToLatest();
    });
    /* Already known before this subscribed — a worker that installed on a
       previous visit and has been waiting since, or one that took over in
       the moment between page load and this effect. Subscribing only to
       what happens next would miss exactly the browsers that are stuck. */
    if ((hasSwTakenOver() || isSwUpdateAvailable()) && !touched) reloadToLatest();

    void navigator.serviceWorker?.getRegistration()
      .then((reg) => reg?.update())
      .catch(() => { /* offline: the copy they have is the copy they get */ });

    return () => {
      unsub();
      window.removeEventListener('input', markTouched, { capture: true });
      window.removeEventListener('change', markTouched, { capture: true });
    };
  }, []);

  /* THE CONDITIONS OF ADMISSION, read live.
   *
   * They already existed — the printed form prints them from
   * reportSchoolSettings/admissionFormText and the school edits them in
   * Admissions → Settings. The online form was the only place an applicant
   * could get all the way to submitting without ever being shown them.
   * Falls back to the shipped defaults so the step is never blank. */
  const [conditions, setConditions] = useState<string[]>(DEFAULT_CONDITIONS);
  useEffect(() => {
    getDoc(doc(db, 'reportSchoolSettings', 'admissionFormText'))
      .then((snap) => {
        const live = resolveAdmissionFormText(
          snap.exists() ? (snap.data() as Partial<AdmissionFormText>) : null,
        );
        setConditions(live.conditions);
      })
      .catch(() => { /* defaults already in state */ });
  }, []);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  /* Generated once, on mount, and carried into the submission — the
     de-duplication trigger keys on it, so a double-tap on Submit is one
     application rather than two. */
  const [clientToken] = useState(
    () => (crypto.randomUUID && crypto.randomUUID())
      || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );


  /* admission-window doc state */
  const [windowSettings, setWindowSettings] = useState<AdmissionWindowSettings | null>(null);
  const [windowLoaded, setWindowLoaded] = useState(false);

  /* form state */
  const [personal, setPersonal] = useState<PersonalState>({
    firstName: '',
    surname: '',
    gender: '',
    dateOfBirth: '',
    placeOfBirth: '',
    nationality: 'Motswana',
    homeLanguage: 'Setswana',
    religion: '',
    idPassportNumber: '',
    studentEmail: '',
    studentPhone: '',
    address: '',
    applyingForForm: '',
    applyingForYear: String(new Date().getFullYear() + 1),
    boardingStatus: 'day',
    notes: '',
  });
  const [guardians, setGuardians] = useState<Guardian[]>([EMPTY_GUARDIAN(true)]);
  const [medical, setMedical] = useState<MedicalState>({
    doctorName: '',
    doctorPhone: '',
    medicalAidProvider: '',
    medicalAidNumber: '',
    allergies: '',
    chronicConditions: '',
    medications: '',
    bloodType: '',
    hasMedicalCondition: false,
    medicalConditionDetails: '',
    emergencyTreatmentConsent: true,
    emergencyTreatmentNoConsentAction: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactEmail: '',
  });
  const [education, setEducation] = useState<EducationState>({
    last: EMPTY_PREV_SCHOOL(),
    others: [],
  });
  const [siblings, setSiblings] = useState<SiblingState>({
    hasSiblingAtSchool: false,
    sibling1: { names: '', surname: '' },
    sibling2: { names: '', surname: '' },
    siblingParentNames: '',
    siblingParentEmail: '',
  });
  const [honeypot, setHoneypot] = useState(''); // anti-bot
  const [touched, setTouched] = useState<Set<string>>(new Set());

  /* submit state */
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ ref: string } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);

  /* ---- load admission-window settings ---- */
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'reportSchoolSettings', 'admissionWindow'));
        if (snap.exists()) {
          setWindowSettings(snap.data() as AdmissionWindowSettings);
        }
      } catch (e) {
        // Public-read should work; if it doesn't, default to "open".
        console.warn('admissionWindow read failed; defaulting to open', e);
      } finally {
        setWindowLoaded(true);
      }
    })();
  }, []);

  /* ---- which forms are available ---- */
  const availableForms = useMemo(() => {
    const allowed = windowSettings?.allowedForms;
    if (!allowed || allowed.length === 0) return FORM_LEVELS;
    return FORM_LEVELS.filter((f) => allowed.includes(f.id));
  }, [windowSettings]);

  const isAcceptingApplications =
    windowSettings == null ||
    windowSettings.acceptingApplications !== false; // default true

  /* ---- validation -------------------------------------------------- */

  const fieldErrors = useMemo(() => {
    const e: Record<string, string | null> = {};
    /** Present-and-not-blank. Paired with the format checks, which pass an
     *  empty string because an empty string is a valid absence. */
    const req = (v: string | undefined, label: string) =>
      (String(v ?? '').trim() ? null : `${label} is required.`);
    e.firstName = validateName(personal.firstName, 'First name');
    e.surname = validateName(personal.surname, 'Surname');
    e.gender = !personal.gender ? 'Please select gender.' : null;
    e.dateOfBirth = !personal.dateOfBirth
      ? 'Date of birth is required.'
      : validateDateOfBirth(personal.dateOfBirth, { minAge: 5, maxAge: 25 });
    e.nationality = !personal.nationality ? 'Nationality is required.' : null;
    e.address = !personal.address.trim()
      ? 'Address is required.'
      : personal.address.trim().length < 5
      ? 'Address is too short.'
      : null;
    e.applyingForForm = !personal.applyingForForm
      ? 'Please select the form being applied for.'
      : null;
    e.applyingForYear = !/^\d{4}$/.test(personal.applyingForYear)
      ? 'Year must be 4 digits.'
      : null;

    /* EVERY FIELD IS REQUIRED NOW.
     *
     * The office was receiving half-complete applications and chasing the
     * rest by phone, which is slower than the form and loses the record of
     * what was asked. Format checks alone let a blank through — an empty
     * string is a valid absence of an email — so each one is paired with a
     * presence check.
     *
     * The exemptions below are CONDITIONAL, not optional: a field about a
     * thing the applicant has said they do not have. Requiring those would
     * make the form unanswerable rather than complete. */
    e.placeOfBirth = req(personal.placeOfBirth, 'Place of birth');
    e.homeLanguage = req(personal.homeLanguage, 'Home language');
    e.religion = req(personal.religion, 'Religion');
    e.idPassportNumber = req(personal.idPassportNumber, 'ID or passport number');
    // A pupil applying to Form 1 may genuinely have neither. One of the two
    // is required so the school can reach them directly if it must.
    e.studentEmail = validateEmail(personal.studentEmail);
    e.studentPhone = validatePhone(personal.studentPhone);
    if (!personal.studentEmail.trim() && !personal.studentPhone.trim()) {
      e.studentContact = 'Give the pupil’s email or phone number.';
    }

    guardians.forEach((g, i) => {
      e[`g${i}_fullName`] = validateName(g.fullName, 'Guardian name');
      // Required for EVERY guardian entered, not just the primary contact —
      // a half-complete second guardian is the record nobody goes back to fix.
      e[`g${i}_occupation`] = validateOccupation(g.occupation);
      e[`g${i}_phone`] = validatePhone(g.phone) ?? req(g.phone, 'Phone');
      e[`g${i}_whatsapp`] = validatePhone(g.whatsapp);
      e[`g${i}_email`] = validateEmail(g.email) ?? req(g.email, 'Email');
      e[`g${i}_idNumber`] = validateIdNumber(g.idNumber) ?? req(g.idNumber, 'ID number');
      e[`g${i}_nationality`] = req(g.nationality, 'Nationality');
      e[`g${i}_employer`] = req(g.employer, 'Employer');
      e[`g${i}_address`] = req(g.address, 'Address');
      // Work and home numbers are genuinely not universal; WhatsApp is
      // checked for format only, for the same reason.
      e[`g${i}_workPhone`] = validatePhone(g.workPhone);
      e[`g${i}_homeTel`] = validatePhone(g.homeTel);
      e[`g${i}_emergencyContactPhone`] = validatePhone(g.emergencyContactPhone);
      if (g.isPrimaryContact && !g.phone && !g.whatsapp && !g.email) {
        e[`g${i}_contact`] =
          'Primary contact must have at least one of phone, WhatsApp, or email.';
      }
    });
    const primaries = guardians.filter((g) => g.isPrimaryContact).length;
    if (primaries === 0) e.primaryGuardian = 'Mark one guardian as primary contact.';
    if (primaries > 1) e.primaryGuardian = 'Only one guardian may be the primary contact.';

    e.emergencyContactName = req(medical.emergencyContactName, 'Emergency contact name');
    e.emergencyContactPhone = validatePhone(medical.emergencyContactPhone)
      ?? req(medical.emergencyContactPhone, 'Emergency contact phone');
    e.emergencyContactEmail = validateEmail(medical.emergencyContactEmail);
    e.doctorName = req(medical.doctorName, "Doctor's name");
    e.doctorPhone = validatePhone(medical.doctorPhone) ?? req(medical.doctorPhone, "Doctor's phone");
    e.bloodType = req(medical.bloodType, 'Blood type');
    // Allergies and medication: an explicit "none" is an answer; a blank is
    // not, and the difference matters in an emergency.
    e.allergies = req(medical.allergies, 'Allergies (write "none" if there are none)');
    e.medications = req(medical.medications, 'Medication (write "none" if there is none)');
    e.chronicConditions = req(medical.chronicConditions, 'Chronic conditions (write "none" if there are none)');
    // Only asked because they ticked the box.
    if (medical.hasMedicalCondition) {
      e.medicalConditionDetails = req(medical.medicalConditionDetails, 'Details of the medical condition');
    }
    if (!medical.emergencyTreatmentConsent) {
      e.emergencyTreatmentNoConsentAction = req(
        medical.emergencyTreatmentNoConsentAction,
        'What the school should do if consent is withheld',
      );
    }

    // Previous schooling: the last school attended is required of everyone.
    e.lastSchoolName = req(education.last.name, 'Last school attended');
    e.lastSchoolYear = req(education.last.year, 'Year at the last school');
    e.lastSchoolFormClass = req(education.last.formClass, 'Form or class at the last school');
    e.lastSchoolContact = req(education.last.contact, 'Contact for the last school');

    // Siblings: only asked because they said there are some.
    e.sibParentEmail = validateEmail(siblings.siblingParentEmail);
    if (siblings.hasSiblingAtSchool) {
      e.sibling1 = req(
        `${siblings.sibling1.names}${siblings.sibling1.surname}`,
        "The sibling's name",
      );
      e.siblingParentNames = req(siblings.siblingParentNames, "The sibling's parent");
    }

    return e;
  }, [personal, guardians, medical, siblings, education]);

  const stepErrors = useMemo(() => {
    const groups: Record<TabId, string[]> = {
      student: [],
      guardians: [],
      education: [],
      medical: [],
      additional: [],
      review: [],
      terms: [],
    };
    /* WHICH STEP AN ERROR BELONGS TO.
     *
     * Named by prefix rather than listed one by one, so a field added later
     * lands on its own step instead of falling through to `student` — which
     * is what the old `else` did, and it would have put every new medical
     * and previous-school error on the first page of the form with nothing
     * to say where the actual problem was. */
    const stepOf = (k: string): TabId => {
      if (/^g\d+_/.test(k) || k === 'primaryGuardian') return 'guardians';
      if (k.startsWith('lastSchool')) return 'education';
      if (
        k.startsWith('emergency') || k.startsWith('doctor') || k === 'bloodType'
        || k === 'allergies' || k === 'medications' || k === 'chronicConditions'
        || k === 'medicalConditionDetails'
      ) return 'medical';
      if (k.startsWith('sib')) return 'additional';
      return 'student';
    };
    for (const [k, v] of Object.entries(fieldErrors)) {
      if (!v) continue;
      groups[stepOf(k)].push(v);
    }
    return groups;
  }, [fieldErrors]);

  const allValid =
    Object.values(fieldErrors).every((v) => !v) && guardians.length > 0;

  /* ---- handlers ---- */

  function markTouched(field: string) {
    setTouched((prev) => {
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  }
  function showError(field: string) {
    return touched.has(field) ? fieldErrors[field] : null;
  }

  function addGuardian() {
    setGuardians((prev) => [...prev, EMPTY_GUARDIAN(prev.length === 0)]);
  }
  function removeGuardian(idx: number) {
    setGuardians((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      // Ensure exactly one primary remains
      if (next.length > 0 && !next.some((g) => g.isPrimaryContact)) {
        next[0] = { ...next[0], isPrimaryContact: true };
      }
      return next;
    });
  }
  function updateGuardian(idx: number, patch: Partial<Guardian>) {
    setGuardians((prev) => prev.map((g, i) => (i === idx ? { ...g, ...patch } : g)));
  }
  function setPrimary(idx: number) {
    setGuardians((prev) =>
      prev.map((g, i) => ({ ...g, isPrimaryContact: i === idx })),
    );
  }

  async function handleSubmit() {
    // mark every field touched so errors show
    const allFields = new Set<string>();
    Object.keys(fieldErrors).forEach((k) => allFields.add(k));
    setTouched(allFields);

    /* Defensive, not decorative. The button is disabled without it, but a
       submit path that does not check would write an application the school
       cannot show was made on the Conditions of Admission. The Firestore
       rule refuses one too — this is the message the parent actually reads. */
    if (!acceptedTerms) {
      setTab('terms');
      setSubmitError('Please read and accept the Conditions of Admission before submitting.');
      return;
    }

    if (!allValid) {
      setSubmitError('Please correct the highlighted fields before submitting.');
      // jump to first invalid tab
      const order: TabId[] = ['student', 'guardians', 'education', 'medical', 'additional'];
      for (const t of order) {
        if (stepErrors[t].length > 0) {
          setTab(t);
          break;
        }
      }
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      // Clean a previous-school row; drop it if entirely empty.
      const cleanPrevSchool = (s: PreviousSchoolEntry) => ({
        name: (s.name || '').trim(),
        year: (s.year || '').trim(),
        formClass: (s.formClass || '').trim(),
        contact: (s.contact || '').trim(),
      });
      const prevLast = cleanPrevSchool(education.last);
      const prevOthers = education.others
        .map(cleanPrevSchool)
        .filter((s) => s.name || s.year || s.formClass || s.contact);

      const payload = {
        _honeypot: honeypot,
        firstName: personal.firstName.trim(),
        surname: personal.surname.trim(),
        gender: personal.gender,
        dateOfBirth: personal.dateOfBirth,
        placeOfBirth: personal.placeOfBirth.trim(),
        nationality: personal.nationality.trim(),
        homeLanguage: personal.homeLanguage.trim(),
        religion: personal.religion.trim(),
        idPassportNumber: personal.idPassportNumber.trim(),
        studentEmail: personal.studentEmail.trim(),
        studentPhone: personal.studentPhone.replace(/[\s().-]/g, ''),
        address: personal.address.trim(),
        applyingForForm: personal.applyingForForm,
        applyingForYear: personal.applyingForYear,
        boardingStatus: personal.boardingStatus,
        // Legacy string kept for backward-compat (mirrors the last school).
        previousSchool: prevLast.name,
        previousSchools: { last: prevLast, others: prevOthers },
        notes: personal.notes.trim(),
        /* WHAT THEY ACTUALLY AGREED TO, snapshotted.
         *
         * The Conditions of Admission are editable by the school, so the
         * live text a year from now may not be the text this parent read.
         * Storing the wording alongside the acknowledgement is the whole
         * point of taking one — an acceptance that only says "yes" and
         * cannot say "to what" proves nothing. The confirmation email is
         * built from this same snapshot, so the two cannot disagree. */
        acceptedTerms: true,
        acceptedTermsAt: new Date().toISOString(),
        acceptedConditions: conditions,
        guardians: guardians.map((g) => ({
          ...g,
          fullName: g.fullName.trim(),
          occupation: g.occupation?.trim() ?? '',
          nationality: g.nationality?.trim() ?? '',
          email: g.email?.trim() ?? '',
          phone: g.phone?.replace(/[\s().-]/g, '') ?? '',
          whatsapp: g.whatsapp?.replace(/[\s().-]/g, '') ?? '',
          workPhone: g.workPhone?.replace(/[\s().-]/g, '') ?? '',
          homeTel: g.homeTel?.replace(/[\s().-]/g, '') ?? '',
          emergencyContactName: g.emergencyContactName?.trim() ?? '',
          emergencyContactPhone: g.emergencyContactPhone?.replace(/[\s().-]/g, '') ?? '',
        })),
        medical: {
          doctorName: medical.doctorName.trim(),
          doctorPhone: medical.doctorPhone.replace(/[\s().-]/g, ''),
          medicalAidProvider: medical.medicalAidProvider.trim(),
          medicalAidNumber: medical.medicalAidNumber.trim(),
          allergies: medical.allergies.trim(),
          chronicConditions: medical.chronicConditions.trim(),
          medications: medical.medications.trim(),
          bloodType: medical.bloodType.trim(),
          hasMedicalCondition: medical.hasMedicalCondition,
          medicalConditionDetails: medical.hasMedicalCondition
            ? medical.medicalConditionDetails.trim()
            : '',
          emergencyTreatmentConsent: medical.emergencyTreatmentConsent,
          emergencyTreatmentNoConsentAction: medical.emergencyTreatmentConsent
            ? ''
            : medical.emergencyTreatmentNoConsentAction.trim(),
        },
        emergencyContactName: medical.emergencyContactName.trim(),
        emergencyContactPhone: medical.emergencyContactPhone.replace(/[\s().-]/g, ''),
        emergencyContactEmail: medical.emergencyContactEmail.trim(),
        siblings: {
          hasSiblingAtSchool: siblings.hasSiblingAtSchool,
          sibling1: {
            names: siblings.sibling1.names.trim(),
            surname: siblings.sibling1.surname.trim(),
          },
          sibling2: {
            names: siblings.sibling2.names.trim(),
            surname: siblings.sibling2.surname.trim(),
          },
          siblingParentNames: siblings.siblingParentNames.trim(),
          siblingParentEmail: siblings.siblingParentEmail.trim(),
        },
      };

      // Path B intake: the applicant writes the application doc directly
      // (public-create rule, shape-validated), then a Cloud Function
      // trigger (processApplicationSubmission) validates fully, assigns
      // the official reference number, and writes it back. We listen to
      // our own doc (capability read by its random id) for that result.
      // No public callable / no public IAM required.
      const now = new Date().toISOString();
      const docData = {
        ...payload,
        status: 'submitting',
        clientToken,
        submittedAt: now,
        updatedAt: now,
      };

      /* THE RULE IS STRICTER THAN THIS FORM, and when it refuses,
         Firestore says "Missing or insufficient permissions" — which
         tells a parent nothing and leaves the school no record at all.
         So the shape the rule requires is checked HERE, where the
         message can name the field. */
      const shapeProblem = submissionShapeProblem(docData);
      if (shapeProblem) throw new Error(shapeProblem);

      /* addDoc resolves on server acknowledgement, so a blocked
         connection leaves it pending forever: nothing throws, nothing
         catches, and the button sits on "Submitting…" until the tab is
         closed. That is what a parent reported. Race it. */
      const created = await withSubmitTimeout(
        addDoc(collection(db, 'reportApplications'), docData),
      );

      // Wait for the trigger to write back a referenceNumber (success or
      // duplicate) or a rejection status. Times out gracefully.
      const outcome = await new Promise<
        { ref: string } | { error: string }
      >((resolve) => {
        let settled = false;
        const finish = (v: { ref: string } | { error: string }) => {
          if (settled) return;
          settled = true;
          unsub();
          clearTimeout(timer);
          resolve(v);
        };
        const unsub = onSnapshot(
          created,
          (snap) => {
            const d = snap.data() as
              | { referenceNumber?: string; status?: string; validationErrors?: string }
              | undefined;
            if (!d) return;
            if (d.referenceNumber && (d.status === 'pending' || d.status === 'duplicate')) {
              finish({ ref: d.referenceNumber });
            } else if (d.status === 'rejected_validation') {
              finish({ error: d.validationErrors || 'Your application could not be validated. Please review your entries.' });
            } else if (d.status === 'spam') {
              // Honeypot tripped — show a generic confirmation, do not reveal.
              finish({ ref: 'APP-XXXX-XXXX' });
            }
          },
          () => finish({ error: 'Submission could not be confirmed. Please try again.' }),
        );
        // 30s safety timeout — the doc is saved either way; tell the user
        // it was received and to await contact if no number appears.
        const timer = setTimeout(
          () => finish({ error: 'Your application was received, but confirming the reference number is taking longer than expected. The admissions office can locate it by your name and email.' }),
          30000,
        );
      });

      if ('error' in outcome) {
        throw new Error(outcome.error);
      }
      setSuccess({ ref: outcome.ref });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e: unknown) {
      setSubmitError(explainSubmitError(e));
      /* THE ERROR USED TO RENDER ABOVE THE FOLD AND THE BUTTON IS AT THE
         BOTTOM. On the review step that is a long way off screen, so a
         refused submission looked to the parent exactly like nothing
         happening. Bring it to them. */
      requestAnimationFrame(() => {
        errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    } finally {
      setSubmitting(false);
    }
  }

  /* ---- render: closed-state ---- */
  if (windowLoaded && !isAcceptingApplications) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1b3a6b] to-[#2d5295] flex items-center justify-center p-6">
        <div className="max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="flex justify-center mb-4">
            <SchoolLogo size={72} />
          </div>
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">
            {SCHOOL.name}
          </p>
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-50 my-3">
            <AlertCircle size={24} className="text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Applications are currently closed
          </h1>
          <p className="text-sm text-gray-600 mb-4">
            {windowSettings?.message ??
              `${SCHOOL.name} is not currently accepting applications. Please check back later or contact the admissions office.`}
          </p>
          <p className="text-xs text-gray-500">
            {SCHOOL.contact.phonePrimary} · {SCHOOL.contact.email}
          </p>
        </div>
      </div>
    );
  }

  /* ---- render: success ---- */
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1b3a6b] to-[#2d5295] flex items-center justify-center p-6">
        <div className="max-w-lg bg-white rounded-2xl shadow-2xl p-10 text-center">
          <div className="flex justify-center mb-4">
            <SchoolLogo size={72} />
          </div>
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">
            {SCHOOL.name}
          </p>
          <p className="text-xs text-[#d4a92c] font-semibold mb-4">
            {SCHOOL.motto}
          </p>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 mb-5">
            <CheckCircle2 size={36} className="text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Application received
          </h1>
          <p className="text-sm text-gray-600 mb-6">
            Thank you for applying to {SCHOOL.name}. We have sent a confirmation
            email to the primary contact's address.
          </p>
          <div className="bg-gradient-to-r from-[#1b3a6b] to-[#2d5295] rounded-xl p-5 mb-6 text-white">
            <p className="text-xs uppercase tracking-widest opacity-75 mb-1">
              Your reference number
            </p>
            <p className="text-2xl font-bold tracking-wider">{success.ref}</p>
            <button
              onClick={() => navigator.clipboard?.writeText(success.ref)}
              className="mt-3 inline-flex items-center gap-1 text-xs opacity-90 hover:opacity-100"
            >
              <Copy size={12} /> Copy
            </button>
          </div>
          <div className="text-left bg-[#1b3a6b]/5 border border-[#1b3a6b]/20 rounded-xl p-4 mb-6 flex items-start gap-2.5">
            <Wallet size={18} className="mt-0.5 shrink-0 text-[#2d5295]" />
            <p className="text-xs text-gray-600 leading-relaxed">
              <strong className="text-gray-900">Next step — fees.</strong> Once your application is
              reviewed and approved, the <strong>application fee</strong> and{' '}
              <strong>registration fee</strong> must be paid before admission can be finalised. The
              admissions office will confirm the amounts and payment details, and your child's place is
              secured only after both fees are received. Please quote your reference number when paying.
            </p>
          </div>
          <p className="text-xs text-gray-500 mb-6">
            Please keep this reference number safe. Our admissions office will
            review your application and respond within 5 working days.
          </p>
          <a
            href={SCHOOL.contact.website ? `https://${SCHOOL.contact.website}` : '/'}
            className="inline-block text-sm text-[#2d5295] hover:underline"
          >
            ← Return to {SCHOOL.contact.website || 'school website'}
          </a>
        </div>
      </div>
    );
  }

  /* ---- render: form ---- */

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* ---- header ---- */}
        <div className="bg-gradient-to-r from-[#1b3a6b] to-[#2d5295] rounded-t-2xl px-6 py-8 text-white">
          <div className="flex items-center gap-4 mb-2">
            <div className="bg-white/10 rounded-xl p-2 backdrop-blur-sm">
              <SchoolLogo size={56} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest opacity-75">
                {SCHOOL.name}
              </p>
              <h1 className="text-2xl font-bold">Application for Admission</h1>
              <p className="text-xs text-[#d4a92c] font-semibold mt-0.5">
                {SCHOOL.motto}
              </p>
            </div>
          </div>
          <p className="text-sm opacity-90 mt-3">
            Apply for a place at {SCHOOL.shortName}. All fields marked with * are
            required.
          </p>
        </div>

        {/* ---- step progress ---- */}
        <div className="bg-white border-x border-gray-200 px-4 py-3 flex gap-1 overflow-x-auto">
          {TABS.map((t, i) => {
            const currentIndex = TABS.findIndex((x) => x.id === tab);
            const isActive = tab === t.id;
            const isDone = i < currentIndex && stepErrors[t.id].length === 0;
            const locked = i > Math.max(currentIndex, maxStep);
            const hasErrors = stepErrors[t.id].length > 0 && touched.size > 0;
            return (
              <button
                key={t.id}
                onClick={() => { if (!locked) setTab(t.id); }}
                disabled={locked}
                aria-current={isActive ? 'step' : undefined}
                title={locked ? 'Complete the current step first' : undefined}
                className={[
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
                  isActive
                    ? 'bg-[#2d5295] text-white'
                    : locked
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100',
                  hasErrors && !isActive ? 'ring-1 ring-red-300' : '',
                ].join(' ')}
              >
                <span
                  className={[
                    'flex items-center justify-center w-5 h-5 rounded-full text-xs',
                    isActive
                      ? 'bg-white/20'
                      : isDone
                      ? 'bg-emerald-100 text-emerald-600'
                      : hasErrors
                      ? 'bg-red-100 text-red-600'
                      : 'bg-gray-200 text-gray-500',
                  ].join(' ')}
                >
                  {isDone ? <CheckCircle2 size={13} /> : i + 1}
                </span>
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* ---- card body ---- */}
        <div className="bg-white border-x border-gray-200 px-6 py-6">
          {submitError && (
            <div ref={errorRef}
              className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{submitError}</span>
            </div>
          )}

          {/* honeypot — visually hidden but in the DOM */}
          <div aria-hidden style={{ position: 'absolute', left: '-9999px' }}>
            <label>
              Leave this field blank
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </label>
          </div>

          {tab === 'student' && (
            <StudentTab
              personal={personal}
              setPersonal={setPersonal}
              availableForms={availableForms}
              showError={showError}
              markTouched={markTouched}
            />
          )}

          {tab === 'guardians' && (
            <GuardiansTab
              guardians={guardians}
              addGuardian={addGuardian}
              removeGuardian={removeGuardian}
              updateGuardian={updateGuardian}
              setPrimary={setPrimary}
              showError={showError}
              markTouched={markTouched}
              primaryError={touched.has('primaryGuardian') ? fieldErrors.primaryGuardian : null}
            />
          )}

          {tab === 'education' && (
            <EducationTab education={education} setEducation={setEducation} />
          )}

          {tab === 'medical' && (
            <MedicalTab
              medical={medical}
              setMedical={setMedical}
              showError={showError}
              markTouched={markTouched}
            />
          )}

          {tab === 'additional' && (
            <AdditionalTab
              siblings={siblings}
              setSiblings={setSiblings}
              notes={personal.notes}
              setNotes={(v) => setPersonal((p) => ({ ...p, notes: v }))}
              showError={showError}
              markTouched={markTouched}
            />
          )}

          {tab === 'terms' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Conditions of Admission</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Please read these before submitting. They are the same conditions printed on
                  the school&rsquo;s application form, and they will be repeated in the email
                  confirming your application.
                </p>
              </div>

              <ol className="space-y-2.5 max-h-96 overflow-y-auto rounded-lg border-2 border-gray-200 bg-gray-50 p-4">
                {conditions.map((c, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-gray-800">
                    <span className="font-semibold text-[#2d5295] shrink-0">{i + 1}.</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ol>

              <label className="flex items-start gap-3 rounded-lg border-2 border-gray-200 p-4 cursor-pointer hover:border-[#2d5295]">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300"
                />
                <span className="text-sm text-gray-800">
                  I declare that the information given in this application is true and complete,
                  and that I have read, understood and accept the Conditions of Admission set out
                  above.
                  <span className="text-red-500"> *</span>
                </span>
              </label>

              {!acceptedTerms && (
                <p className="text-xs text-gray-500">
                  The application cannot be submitted until this is acknowledged.
                </p>
              )}
            </div>
          )}

          {tab === 'review' && (
            <ReviewTab
              personal={personal}
              guardians={guardians}
              medical={medical}
              education={education}
              siblings={siblings}
              fieldErrors={fieldErrors}
              setTab={setTab}
            />
          )}
        </div>

        {/* ---- footer / nav ---- */}
        <div className="bg-white border border-gray-200 rounded-b-2xl px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => {
              const idx = TABS.findIndex((t) => t.id === tab);
              if (idx > 0) setTab(TABS[idx - 1].id);
            }}
            disabled={TABS.findIndex((t) => t.id === tab) === 0}
            className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={16} />
            Back
          </button>

          <div className="text-xs text-gray-500">
            Step {TABS.findIndex((t) => t.id === tab) + 1} of {TABS.length}
          </div>

          {tab !== 'terms' ? (
            <button
              onClick={() => {
                const idx = TABS.findIndex((t) => t.id === tab);
                if (stepErrors[tab].length > 0) {
                  // Reveal what's missing on this step and don't advance.
                  setTouched(new Set(Object.keys(fieldErrors)));
                  setSubmitError('Please complete the required fields on this step before continuing.');
                  return;
                }
                setSubmitError(null);
                if (idx < TABS.length - 1) { setTab(TABS[idx + 1].id); setMaxStep((m) => Math.max(m, idx + 1)); }
              }}
              className="flex items-center gap-1 px-4 py-2 bg-[#2d5295] text-white text-sm font-semibold rounded-lg hover:bg-[#1b3a6b]"
            >
              Continue
              <ChevronRight size={16} />
            </button>
          ) : (
            /* The error also lives at the top of the card, which on the
               review step is far off screen — the parent is down here,
               looking at the button. */
            <div className="flex flex-col items-stretch sm:items-end gap-2">
              {submitError && (
                <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 max-w-md">
                  {submitError}
                </p>
              )}
              <button
                onClick={handleSubmit}
                disabled={submitting || !allValid || !acceptedTerms}
                className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#1b3a6b] to-[#2d5295] text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {submitting ? 'Submitting…' : 'Submit application'}
              </button>
            </div>
          )}
        </div>

        {/* ---- footer info ---- */}
        <p className="mt-6 text-center text-xs text-gray-500">
          {SCHOOL.name} · {SCHOOL.contact.phonePrimary} · {SCHOOL.contact.email}
        </p>
      </div>
    </div>
  );
}

/* ================================================================
   STUDENT TAB
   ================================================================ */
function StudentTab({
  personal,
  setPersonal,
  availableForms,
  showError,
  markTouched,
}: {
  personal: PersonalState;
  setPersonal: React.Dispatch<React.SetStateAction<PersonalState>>;
  availableForms: { id: string; label: string }[];
  showError: (k: string) => string | null;
  markTouched: (k: string) => void;
}) {
  function set<K extends keyof PersonalState>(k: K, v: PersonalState[K]) {
    setPersonal((p) => ({ ...p, [k]: v }));
  }
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <User size={20} className="text-[#2d5295]" />
        Student details
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel required>First name</FieldLabel>
          <input
            type="text"
            value={personal.firstName}
            onChange={(e) => set('firstName', e.target.value)}
            onBlur={() => markTouched('firstName')}
            className={inputClass(!!showError('firstName'))}
            placeholder="Tebogo"
          />
          <ErrorText msg={showError('firstName')} />
        </div>
        <div>
          <FieldLabel required>Surname</FieldLabel>
          <input
            type="text"
            value={personal.surname}
            onChange={(e) => set('surname', e.target.value)}
            onBlur={() => markTouched('surname')}
            className={inputClass(!!showError('surname'))}
            placeholder="Modise"
          />
          <ErrorText msg={showError('surname')} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel required>Gender</FieldLabel>
          <div className="flex gap-2">
            {[
              { v: 'M', label: 'Male' },
              { v: 'F', label: 'Female' },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => {
                  set('gender', opt.v as 'M' | 'F');
                  markTouched('gender');
                }}
                className={[
                  'flex-1 py-2.5 rounded-lg border-2 text-sm font-medium transition-colors',
                  personal.gender === opt.v
                    ? 'border-[#2d5295] bg-[#2d5295]/10 text-[#2d5295]'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300',
                ].join(' ')}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <ErrorText msg={showError('gender')} />
        </div>
        <div>
          <FieldLabel required icon={Calendar}>
            Date of birth
          </FieldLabel>
          <input
            type="date"
            value={personal.dateOfBirth}
            onChange={(e) => set('dateOfBirth', e.target.value)}
            onBlur={() => markTouched('dateOfBirth')}
            max={new Date().toISOString().slice(0, 10)}
            className={inputClass(!!showError('dateOfBirth'))}
          />
          <ErrorText msg={showError('dateOfBirth')} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel required icon={MapPin}>Place of birth</FieldLabel>
          <input
            type="text"
            value={personal.placeOfBirth}
            onChange={(e) => set('placeOfBirth', e.target.value)}
            className={inputClass(!!showError('placeOfBirth'))}
            placeholder="e.g. Gaborone"
          />
          <ErrorText msg={showError('placeOfBirth')} />
        </div>
        <div>
          <FieldLabel required icon={Globe}>
            Nationality
          </FieldLabel>
          <input
            type="text"
            value={personal.nationality}
            onChange={(e) => set('nationality', e.target.value)}
            onBlur={() => markTouched('nationality')}
            className={inputClass(!!showError('nationality'))}
            placeholder="Motswana"
          />
          <ErrorText msg={showError('nationality')} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel required>Home language</FieldLabel>
          <input
            type="text"
            value={personal.homeLanguage}
            onChange={(e) => set('homeLanguage', e.target.value)}
            className={inputClass(!!showError('homeLanguage'))}
            placeholder="Setswana / English"
          />
          <ErrorText msg={showError('homeLanguage')} />
        </div>
        <div>
          <FieldLabel required>Religion</FieldLabel>
          <input
            type="text"
            value={personal.religion}
            onChange={(e) => set('religion', e.target.value)}
            className={inputClass(!!showError('religion'))}
            placeholder="Optional"
          />
          <ErrorText msg={showError('religion')} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <FieldLabel required icon={IdCard}>ID / passport no.</FieldLabel>
          <input
            type="text"
            value={personal.idPassportNumber}
            onChange={(e) => set('idPassportNumber', e.target.value)}
            className={inputClass(!!showError('idPassportNumber'))}
            placeholder="Student's own"
          />
          <ErrorText msg={showError('idPassportNumber')} />
        </div>
        <div>
          <FieldLabel icon={Mail}>Student email</FieldLabel>
          <input
            type="email"
            value={personal.studentEmail}
            onChange={(e) => set('studentEmail', e.target.value)}
            onBlur={() => markTouched('studentEmail')}
            className={inputClass(!!showError('studentEmail'))}
            placeholder="Optional"
          />
          <ErrorText msg={showError('studentEmail')} />
        </div>
        <div>
          <FieldLabel icon={Phone}>Student phone</FieldLabel>
          <input
            type="tel"
            value={personal.studentPhone}
            onChange={(e) => set('studentPhone', e.target.value)}
            onBlur={() => markTouched('studentPhone')}
            className={inputClass(!!showError('studentPhone'))}
            placeholder="Optional"
          />
          <ErrorText msg={showError('studentPhone')} />
        </div>
      </div>

      <div>
        <FieldLabel required icon={Home}>
          Address
        </FieldLabel>
        <textarea
          value={personal.address}
          onChange={(e) => set('address', e.target.value)}
          onBlur={() => markTouched('address')}
          className={inputClass(!!showError('address'))}
          rows={2}
          placeholder="Plot, neighbourhood, city"
        />
        <ErrorText msg={showError('address')} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <FieldLabel required icon={BookOpen}>
            Applying for form
          </FieldLabel>
          <select
            value={personal.applyingForForm}
            onChange={(e) => set('applyingForForm', e.target.value)}
            onBlur={() => markTouched('applyingForForm')}
            className={inputClass(!!showError('applyingForForm'))}
          >
            <option value="">Select…</option>
            {availableForms.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
          <ErrorText msg={showError('applyingForForm')} />
        </div>
        <div>
          <FieldLabel required>Year</FieldLabel>
          <input
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={personal.applyingForYear}
            onChange={(e) => set('applyingForYear', e.target.value.replace(/\D/g, ''))}
            onBlur={() => markTouched('applyingForYear')}
            className={inputClass(!!showError('applyingForYear'))}
          />
          <ErrorText msg={showError('applyingForYear')} />
        </div>
        <div>
          <FieldLabel>Boarding status</FieldLabel>
          <select
            value={personal.boardingStatus}
            onChange={(e) => set('boardingStatus', e.target.value as 'day' | 'boarding')}
            className={inputClass()}
          >
            <option value="day">Day scholar</option>
            <option value="boarding">Boarder</option>
          </select>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
   GUARDIANS TAB
   ================================================================ */
function GuardiansTab({
  guardians,
  addGuardian,
  removeGuardian,
  updateGuardian,
  setPrimary,
  showError,
  markTouched,
  primaryError,
}: {
  guardians: Guardian[];
  addGuardian: () => void;
  removeGuardian: (i: number) => void;
  updateGuardian: (i: number, p: Partial<Guardian>) => void;
  setPrimary: (i: number) => void;
  showError: (k: string) => string | null;
  markTouched: (k: string) => void;
  primaryError?: string | null;
}) {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Users size={20} className="text-[#2d5295]" />
          Parents / Guardians
        </h2>
        <button
          type="button"
          onClick={addGuardian}
          className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
        >
          <Plus size={14} /> Add guardian
        </button>
      </div>

      <p className="text-xs text-gray-500">
        Add at least one guardian. Mark one as the primary contact — we'll
        send the application acknowledgement and updates to them.
      </p>

      {primaryError && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          {primaryError}
        </div>
      )}

      <div className="space-y-4">
        {guardians.map((g, i) => (
          <GuardianBlock
            key={g.id}
            guardian={g}
            index={i}
            removable={guardians.length > 1}
            onRemove={() => removeGuardian(i)}
            onUpdate={(p) => updateGuardian(i, p)}
            onSetPrimary={() => setPrimary(i)}
            showError={showError}
            markTouched={markTouched}
          />
        ))}
      </div>
    </div>
  );
}

function GuardianBlock({
  guardian,
  index,
  removable,
  onRemove,
  onUpdate,
  onSetPrimary,
  showError,
  markTouched,
}: {
  guardian: Guardian;
  index: number;
  removable: boolean;
  onRemove: () => void;
  onUpdate: (p: Partial<Guardian>) => void;
  onSetPrimary: () => void;
  showError: (k: string) => string | null;
  markTouched: (k: string) => void;
}) {
  const k = (f: string) => `g${index}_${f}`;
  return (
    <div
      className={[
        'border-2 rounded-xl p-4 transition-colors',
        guardian.isPrimaryContact ? 'border-[#2d5295]/30 bg-[#2d5295]/5' : 'border-gray-200',
      ].join(' ')}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">
            Guardian {index + 1}
          </span>
          {guardian.isPrimaryContact && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#2d5295] text-white">
              <Star size={10} fill="currentColor" /> Primary
            </span>
          )}
          {!guardian.isPrimaryContact && (
            <button
              type="button"
              onClick={onSetPrimary}
              className="text-xs text-[#2d5295] hover:underline"
            >
              Make primary
            </button>
          )}
        </div>
        {removable && (
          <button
            type="button"
            onClick={onRemove}
            className="text-gray-400 hover:text-red-600"
            title="Remove guardian"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <FieldLabel required>Full name</FieldLabel>
          <input
            type="text"
            value={guardian.fullName}
            onChange={(e) => onUpdate({ fullName: e.target.value })}
            onBlur={() => markTouched(k('fullName'))}
            className={inputClass(!!showError(k('fullName')))}
          />
          <ErrorText msg={showError(k('fullName'))} />
        </div>
        <div>
          <FieldLabel>Relationship</FieldLabel>
          <select
            value={guardian.role}
            onChange={(e) => onUpdate({ role: e.target.value as GuardianRole })}
            className={inputClass()}
          >
            {Object.entries(GUARDIAN_ROLE_LABELS).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <FieldLabel icon={Phone}>Phone</FieldLabel>
          <input
            type="tel"
            value={guardian.phone ?? ''}
            onChange={(e) => onUpdate({ phone: e.target.value })}
            onBlur={() => markTouched(k('phone'))}
            className={inputClass(!!showError(k('phone')))}
            placeholder="+267 71 234 567"
          />
          <ErrorText msg={showError(k('phone'))} />
        </div>
        <div>
          <FieldLabel icon={MessageCircle}>WhatsApp</FieldLabel>
          <input
            type="tel"
            value={guardian.whatsapp ?? ''}
            onChange={(e) => onUpdate({ whatsapp: e.target.value })}
            onBlur={() => markTouched(k('whatsapp'))}
            className={inputClass(!!showError(k('whatsapp')))}
            placeholder="+267 71 234 567"
          />
          <ErrorText msg={showError(k('whatsapp'))} />
        </div>

        <div>
          <FieldLabel icon={Building2}>Work phone</FieldLabel>
          <input
            type="tel"
            value={guardian.workPhone ?? ''}
            onChange={(e) => onUpdate({ workPhone: e.target.value })}
            onBlur={() => markTouched(k('workPhone'))}
            className={inputClass(!!showError(k('workPhone')))}
            placeholder="+267 … (optional)"
          />
          <ErrorText msg={showError(k('workPhone'))} />
        </div>
        <div>
          <FieldLabel icon={Home}>Home tel</FieldLabel>
          <input
            type="tel"
            value={guardian.homeTel ?? ''}
            onChange={(e) => onUpdate({ homeTel: e.target.value })}
            onBlur={() => markTouched(k('homeTel'))}
            className={inputClass(!!showError(k('homeTel')))}
            placeholder="+267 … (optional)"
          />
          <ErrorText msg={showError(k('homeTel'))} />
        </div>

        <div>
          <FieldLabel icon={Mail}>Email</FieldLabel>
          <input
            type="email"
            value={guardian.email ?? ''}
            onChange={(e) => onUpdate({ email: e.target.value })}
            onBlur={() => markTouched(k('email'))}
            className={inputClass(!!showError(k('email')))}
            placeholder="parent@example.com"
          />
          <ErrorText msg={showError(k('email'))} />
        </div>
        <div>
          <FieldLabel icon={Shield}>ID / passport number</FieldLabel>
          <input
            type="text"
            value={guardian.idNumber ?? ''}
            onChange={(e) => onUpdate({ idNumber: e.target.value })}
            onBlur={() => markTouched(k('idNumber'))}
            className={inputClass(!!showError(k('idNumber')))}
          />
          <ErrorText msg={showError(k('idNumber'))} />
        </div>

        <div>
          <FieldLabel icon={Globe}>Nationality</FieldLabel>
          <input
            type="text"
            value={guardian.nationality ?? ''}
            onChange={(e) => onUpdate({ nationality: e.target.value })}
            className={inputClass()}
            placeholder="Optional"
          />
        </div>
        <div>
          <FieldLabel icon={Briefcase} required>Occupation</FieldLabel>
          <input
            type="text"
            value={guardian.occupation ?? ''}
            onChange={(e) => onUpdate({ occupation: e.target.value })}
            onBlur={() => markTouched(k('occupation'))}
            className={inputClass(!!showError(k('occupation')))}
          />
          <ErrorText msg={showError(k('occupation'))} />
        </div>
        <div>
          <FieldLabel icon={Building2}>Employer</FieldLabel>
          <input
            type="text"
            value={guardian.employer ?? ''}
            onChange={(e) => onUpdate({ employer: e.target.value })}
            className={inputClass()}
          />
        </div>

        <div className="sm:col-span-2">
          <FieldLabel icon={MapPin}>Address (if different from student)</FieldLabel>
          <input
            type="text"
            value={guardian.address ?? ''}
            onChange={(e) => onUpdate({ address: e.target.value })}
            className={inputClass()}
            placeholder="Optional"
          />
        </div>
      </div>

      <ErrorText msg={showError(k('contact'))} />

      <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!guardian.responsibleForFees}
            onChange={(e) => onUpdate({ responsibleForFees: e.target.checked })}
            className="rounded text-[#2d5295] focus:ring-[#2d5295]"
          />
          <span className="flex items-center gap-1.5">
            <Wallet size={14} className="text-gray-400" />
            Responsible for paying school fees
          </span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <FieldLabel>Emergency contact name</FieldLabel>
            <input
              type="text"
              value={guardian.emergencyContactName ?? ''}
              onChange={(e) => onUpdate({ emergencyContactName: e.target.value })}
              className={inputClass()}
              placeholder="Optional"
            />
          </div>
          <div>
            <FieldLabel icon={Phone}>Emergency contact phone</FieldLabel>
            <input
              type="tel"
              value={guardian.emergencyContactPhone ?? ''}
              onChange={(e) => onUpdate({ emergencyContactPhone: e.target.value })}
              onBlur={() => markTouched(k('emergencyContactPhone'))}
              className={inputClass(!!showError(k('emergencyContactPhone')))}
              placeholder="+267 … (optional)"
            />
            <ErrorText msg={showError(k('emergencyContactPhone'))} />
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-col sm:flex-row gap-3 pt-3 border-t border-gray-100">
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={!!guardian.consentToSend}
            onChange={(e) => onUpdate({ consentToSend: e.target.checked })}
            className="rounded text-[#2d5295] focus:ring-[#2d5295]"
          />
          Consent to receive emails about my child
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={!!guardian.consentToWhatsapp}
            onChange={(e) => onUpdate({ consentToWhatsapp: e.target.checked })}
            className="rounded text-[#2d5295] focus:ring-[#2d5295]"
          />
          Consent to receive WhatsApp messages
        </label>
      </div>
    </div>
  );
}

/* ================================================================
   MEDICAL TAB
   ================================================================ */
function MedicalTab({
  medical,
  setMedical,
  showError,
  markTouched,
}: {
  medical: MedicalState;
  setMedical: React.Dispatch<React.SetStateAction<MedicalState>>;
  showError: (k: string) => string | null;
  markTouched: (k: string) => void;
}) {
  function set<K extends keyof MedicalState>(k: K, v: MedicalState[K]) {
    setMedical((m) => ({ ...m, [k]: v }));
  }
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <Stethoscope size={20} className="text-[#2d5295]" />
        Medical & emergency
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel required>Doctor's name</FieldLabel>
          <input
            type="text"
            value={medical.doctorName}
            onChange={(e) => set('doctorName', e.target.value)}
            className={inputClass(!!showError('doctorName'))}
            placeholder="Optional"
          />
          <ErrorText msg={showError('doctorName')} />
        </div>
        <div>
          <FieldLabel required icon={Phone}>Doctor's phone</FieldLabel>
          <input
            type="tel"
            value={medical.doctorPhone}
            onChange={(e) => set('doctorPhone', e.target.value)}
            onBlur={() => markTouched('doctorPhone')}
            className={inputClass(!!showError('doctorPhone'))}
            placeholder="+267 …"
          />
          <ErrorText msg={showError('doctorPhone')} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel>Medical aid provider</FieldLabel>
          <input
            type="text"
            value={medical.medicalAidProvider}
            onChange={(e) => set('medicalAidProvider', e.target.value)}
            className={inputClass()}
            placeholder="e.g. BPOMAS, BOMAID"
          />
        </div>
        <div>
          <FieldLabel>Medical aid number</FieldLabel>
          <input
            type="text"
            value={medical.medicalAidNumber}
            onChange={(e) => set('medicalAidNumber', e.target.value)}
            className={inputClass()}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel required>Allergies</FieldLabel>
          <input
            type="text"
            value={medical.allergies}
            onChange={(e) => set('allergies', e.target.value)}
            className={inputClass(!!showError('allergies'))}
            placeholder="Peanuts, dust, …"
          />
          <ErrorText msg={showError('allergies')} />
        </div>
        <div>
          <FieldLabel required>Chronic conditions</FieldLabel>
          <input
            type="text"
            value={medical.chronicConditions}
            onChange={(e) => set('chronicConditions', e.target.value)}
            className={inputClass(!!showError('chronicConditions'))}
            placeholder="Asthma, …"
          />
          <ErrorText msg={showError('chronicConditions')} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel required>Current medications</FieldLabel>
          <input
            type="text"
            value={medical.medications}
            onChange={(e) => set('medications', e.target.value)}
            className={inputClass(!!showError('medications'))}
          />
          <ErrorText msg={showError('medications')} />
        </div>
        <div>
          <FieldLabel required>Blood type</FieldLabel>
          <select
            value={medical.bloodType}
            onChange={(e) => set('bloodType', e.target.value)}
            className={inputClass(!!showError('bloodType'))}
          >
            <option value="">Unknown</option>
            {['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-'].map((bt) => (
              <option key={bt} value={bt}>
                {bt}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="pt-4 border-t border-gray-100 space-y-3">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={medical.hasMedicalCondition}
            onChange={(e) => set('hasMedicalCondition', e.target.checked)}
            className="rounded text-[#2d5295] focus:ring-[#2d5295]"
          />
          <ErrorText msg={showError('bloodType')} />
          The child has a medical condition we should know about
        </label>
        {medical.hasMedicalCondition && (
          <div>
            <FieldLabel>Please explain</FieldLabel>
            <textarea
              value={medical.medicalConditionDetails}
              onChange={(e) => set('medicalConditionDetails', e.target.value)}
              className={inputClass()}
              rows={2}
              placeholder="Condition, treatment, triggers, etc."
            />
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-gray-100 space-y-3">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={medical.emergencyTreatmentConsent}
            onChange={(e) => set('emergencyTreatmentConsent', e.target.checked)}
            className="rounded text-[#2d5295] focus:ring-[#2d5295]"
          />
          I consent to the school arranging emergency medical treatment /
          transport if a guardian cannot be reached
        </label>
        {!medical.emergencyTreatmentConsent && (
          <div>
            <FieldLabel>If not, what should the school do?</FieldLabel>
            <textarea
              value={medical.emergencyTreatmentNoConsentAction}
              onChange={(e) => set('emergencyTreatmentNoConsentAction', e.target.value)}
              className={inputClass()}
              rows={2}
              placeholder="Preferred action in an emergency"
            />
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Emergency contact (other than guardians)
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <FieldLabel required>Contact name</FieldLabel>
            <input
              type="text"
              value={medical.emergencyContactName}
              onChange={(e) => set('emergencyContactName', e.target.value)}
              className={inputClass(!!showError('emergencyContactName'))}
            />
          <ErrorText msg={showError('emergencyContactName')} />
          </div>
          <div>
            <FieldLabel required icon={Phone}>Contact phone</FieldLabel>
            <input
              type="tel"
              value={medical.emergencyContactPhone}
              onChange={(e) => set('emergencyContactPhone', e.target.value)}
              onBlur={() => markTouched('emergencyContactPhone')}
              className={inputClass(!!showError('emergencyContactPhone'))}
              placeholder="+267 …"
            />
            <ErrorText msg={showError('emergencyContactPhone')} />
          </div>
          <div>
            <FieldLabel icon={Mail}>Contact email</FieldLabel>
            <input
              type="email"
              value={medical.emergencyContactEmail}
              onChange={(e) => set('emergencyContactEmail', e.target.value)}
              onBlur={() => markTouched('emergencyContactEmail')}
              className={inputClass(!!showError('emergencyContactEmail'))}
              placeholder="Optional"
            />
            <ErrorText msg={showError('emergencyContactEmail')} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
   EDUCATION TAB (previous schools — paper §4)
   ================================================================ */
function SchoolFields({
  entry,
  onUpdate,
}: {
  entry: PreviousSchoolEntry;
  onUpdate: (p: Partial<PreviousSchoolEntry>) => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="sm:col-span-2">
        <FieldLabel>School name</FieldLabel>
        <input
          type="text"
          value={entry.name ?? ''}
          onChange={(e) => onUpdate({ name: e.target.value })}
          className={inputClass()}
          placeholder="Name of school"
        />
      </div>
      <div>
        <FieldLabel>Year attended</FieldLabel>
        <input
          type="text"
          value={entry.year ?? ''}
          onChange={(e) => onUpdate({ year: e.target.value })}
          className={inputClass()}
          placeholder="e.g. 2025"
        />
      </div>
      <div>
        <FieldLabel>Form / class</FieldLabel>
        <input
          type="text"
          value={entry.formClass ?? ''}
          onChange={(e) => onUpdate({ formClass: e.target.value })}
          className={inputClass()}
          placeholder="e.g. Std 7 / Form 2"
        />
      </div>
      <div className="sm:col-span-2">
        <FieldLabel>School contact</FieldLabel>
        <input
          type="text"
          value={entry.contact ?? ''}
          onChange={(e) => onUpdate({ contact: e.target.value })}
          className={inputClass()}
          placeholder="Phone / email / head's name (optional)"
        />
      </div>
    </div>
  );
}

function EducationTab({
  education,
  setEducation,
}: {
  education: EducationState;
  setEducation: React.Dispatch<React.SetStateAction<EducationState>>;
}) {
  function setLast(patch: Partial<PreviousSchoolEntry>) {
    setEducation((ed) => ({ ...ed, last: { ...ed.last, ...patch } }));
  }
  function addOther() {
    setEducation((ed) =>
      ed.others.length >= 3 ? ed : { ...ed, others: [...ed.others, EMPTY_PREV_SCHOOL()] },
    );
  }
  function removeOther(i: number) {
    setEducation((ed) => ({ ...ed, others: ed.others.filter((_, idx) => idx !== i) }));
  }
  function setOther(i: number, patch: Partial<PreviousSchoolEntry>) {
    setEducation((ed) => ({
      ...ed,
      others: ed.others.map((o, idx) => (idx === i ? { ...o, ...patch } : o)),
    }));
  }
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <GraduationCap size={20} className="text-[#2d5295]" />
        Previous schools
      </h2>
      <p className="text-xs text-gray-500">
        Tell us where the student has studied before. All fields are optional —
        fill in what you can.
      </p>

      <div className="border-2 rounded-xl p-4 border-[#2d5295]/30 bg-[#2d5295]/5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Most recent (last) school
        </h3>
        <SchoolFields entry={education.last} onUpdate={setLast} />
      </div>

      {education.others.map((o, i) => (
        <div key={i} className="border-2 border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">
              Earlier school {i + 1}
            </h3>
            <button
              type="button"
              onClick={() => removeOther(i)}
              className="text-gray-400 hover:text-red-600"
              title="Remove school"
            >
              <Trash2 size={14} />
            </button>
          </div>
          <SchoolFields entry={o} onUpdate={(p) => setOther(i, p)} />
        </div>
      ))}

      {education.others.length < 3 && (
        <button
          type="button"
          onClick={addOther}
          className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
        >
          <Plus size={14} /> Add an earlier school
        </button>
      )}
    </div>
  );
}

/* ================================================================
   ADDITIONAL TAB (siblings — paper §6 — + notes)
   ================================================================ */
function AdditionalTab({
  siblings,
  setSiblings,
  notes,
  setNotes,
  showError,
  markTouched,
}: {
  siblings: SiblingState;
  setSiblings: React.Dispatch<React.SetStateAction<SiblingState>>;
  notes: string;
  setNotes: (v: string) => void;
  showError: (k: string) => string | null;
  markTouched: (k: string) => void;
}) {
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <UserPlus size={20} className="text-[#2d5295]" />
        Additional information
      </h2>

      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={siblings.hasSiblingAtSchool}
            onChange={(e) =>
              setSiblings((s) => ({ ...s, hasSiblingAtSchool: e.target.checked }))
            }
            className="rounded text-[#2d5295] focus:ring-[#2d5295]"
          />
          Another child from this family already attends the school
        </label>

        {siblings.hasSiblingAtSchool && (
          <div className="border-2 border-gray-200 rounded-xl p-4 space-y-4">
            {([1, 2] as const).map((n) => {
              const key = n === 1 ? 'sibling1' : 'sibling2';
              const sib = siblings[key];
              return (
                <div key={n} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <FieldLabel>{`Sibling ${n} — name(s)`}</FieldLabel>
                    <input
                      type="text"
                      value={sib.names}
                      onChange={(e) =>
                        setSiblings((s) => ({
                          ...s,
                          [key]: { ...s[key], names: e.target.value },
                        }))
                      }
                      className={inputClass()}
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <FieldLabel>{`Sibling ${n} — surname`}</FieldLabel>
                    <input
                      type="text"
                      value={sib.surname}
                      onChange={(e) =>
                        setSiblings((s) => ({
                          ...s,
                          [key]: { ...s[key], surname: e.target.value },
                        }))
                      }
                      className={inputClass()}
                      placeholder="Optional"
                    />
                  </div>
                </div>
              );
            })}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <FieldLabel>Parent / guardian name(s)</FieldLabel>
                <input
                  type="text"
                  value={siblings.siblingParentNames}
                  onChange={(e) =>
                    setSiblings((s) => ({ ...s, siblingParentNames: e.target.value }))
                  }
                  className={inputClass()}
                  placeholder="Optional"
                />
              </div>
              <div>
                <FieldLabel icon={Mail}>Parent / guardian email</FieldLabel>
                <input
                  type="email"
                  value={siblings.siblingParentEmail}
                  onChange={(e) =>
                    setSiblings((s) => ({ ...s, siblingParentEmail: e.target.value }))
                  }
                  onBlur={() => markTouched('sibParentEmail')}
                  className={inputClass(!!showError('sibParentEmail'))}
                  placeholder="Optional"
                />
                <ErrorText msg={showError('sibParentEmail')} />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-gray-100">
        <FieldLabel>Additional notes</FieldLabel>
        {/* CAPPED, BECAUSE THE SECURITY RULE CAPS IT. Without a limit
            here a parent could write past 2000 characters and have the
            whole application refused by Firestore with nothing to
            explain why. SUBMISSION_LIMITS.notes is the same number. */}
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, SUBMISSION_LIMITS.notes))}
          className={inputClass()}
          rows={3}
          maxLength={SUBMISSION_LIMITS.notes}
          placeholder="Anything else we should know? (Optional)"
        />
        <p className="mt-1 text-xs text-gray-400 text-right">
          {notes.length} / {SUBMISSION_LIMITS.notes}
        </p>
      </div>
    </div>
  );
}

/* ================================================================
   REVIEW TAB
   ================================================================ */
function ReviewTab({
  personal,
  guardians,
  medical,
  education,
  siblings,
  fieldErrors,
  setTab,
}: {
  personal: PersonalState;
  guardians: Guardian[];
  medical: MedicalState;
  education: EducationState;
  siblings: SiblingState;
  fieldErrors: Record<string, string | null>;
  setTab: (t: TabId) => void;
}) {
  const errCount = Object.values(fieldErrors).filter(Boolean).length;
  const formLabel = FORM_LEVELS.find((f) => f.id === personal.applyingForForm)?.label ?? '—';
  const primary = guardians.find((g) => g.isPrimaryContact);
  const prevSchoolNames = [education.last, ...education.others]
    .map((s) => (s.name || '').trim())
    .filter(Boolean);
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <ClipboardList size={20} className="text-[#2d5295]" />
        Review & submit
      </h2>

      {errCount > 0 ? (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">{errCount} field(s) need attention.</p>
            <p className="text-xs mt-0.5">Go back through the steps to fix the highlighted entries.</p>
          </div>
        </div>
      ) : (
        <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 flex items-start gap-2">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <span>All fields look good. Ready to submit.</span>
        </div>
      )}

      <div className="p-3.5 rounded-lg bg-[#1b3a6b]/5 border border-[#1b3a6b]/20 text-sm text-gray-700 flex items-start gap-2.5">
        <Wallet size={18} className="mt-0.5 shrink-0 text-[#2d5295]" />
        <div>
          <p className="font-semibold text-gray-900">Application &amp; registration fees</p>
          <p className="text-xs mt-1 text-gray-600 leading-relaxed">
            Submitting this form does not secure a place. A one-time{' '}
            <strong>application fee</strong> and <strong>registration fee</strong> are payable, and your
            child can only be admitted once <strong>both have been received</strong>. After we review and
            approve the application, the admissions office will confirm the exact amounts and payment
            details. Please keep your reference number for all correspondence and payments.
          </p>
        </div>
      </div>

      <ReviewBlock
        title="Student"
        onEdit={() => setTab('student')}
        rows={[
          ['Name', `${personal.firstName} ${personal.surname}`.trim() || '—'],
          ['Gender', personal.gender === 'M' ? 'Male' : personal.gender === 'F' ? 'Female' : '—'],
          ['Date of birth', personal.dateOfBirth || '—'],
          ['Place of birth', personal.placeOfBirth || '—'],
          ['Nationality', personal.nationality || '—'],
          ['ID / passport', personal.idPassportNumber || '—'],
          ['Student contact', [personal.studentEmail, personal.studentPhone].filter(Boolean).join(' · ') || '—'],
          ['Address', personal.address || '—'],
          ['Applying for', `${formLabel} (${personal.applyingForYear})`],
          ['Boarding', personal.boardingStatus === 'boarding' ? 'Boarder' : 'Day scholar'],
        ]}
      />
      <ReviewBlock
        title={`Guardians (${guardians.length})`}
        onEdit={() => setTab('guardians')}
        rows={guardians.map((g) => [
          `${GUARDIAN_ROLE_LABELS[g.role]}${g.isPrimaryContact ? ' (primary)' : ''}${g.responsibleForFees ? ' · fees' : ''}`,
          `${g.fullName || '—'}${g.occupation ? ` · ${g.occupation}` : ''}${g.email ? ` · ${g.email}` : ''}${g.phone ? ` · ${g.phone}` : ''}`,
        ])}
      />
      <ReviewBlock
        title="Previous schools"
        onEdit={() => setTab('education')}
        rows={[['Schools', prevSchoolNames.length ? prevSchoolNames.join(', ') : '—']]}
      />
      <ReviewBlock
        title="Medical & emergency"
        onEdit={() => setTab('medical')}
        rows={[
          ['Doctor', medical.doctorName ? `${medical.doctorName}${medical.doctorPhone ? ` · ${medical.doctorPhone}` : ''}` : '—'],
          ['Medical aid', medical.medicalAidProvider ? `${medical.medicalAidProvider}${medical.medicalAidNumber ? ` (${medical.medicalAidNumber})` : ''}` : '—'],
          ['Allergies', medical.allergies || '—'],
          ['Chronic conditions', medical.chronicConditions || '—'],
          ['Medical condition', medical.hasMedicalCondition ? (medical.medicalConditionDetails || 'Yes') : 'None reported'],
          ['Emergency treatment', medical.emergencyTreatmentConsent ? 'Consent given' : `Not consented${medical.emergencyTreatmentNoConsentAction ? ` — ${medical.emergencyTreatmentNoConsentAction}` : ''}`],
          ['Emergency contact', medical.emergencyContactName ? [medical.emergencyContactName, medical.emergencyContactPhone, medical.emergencyContactEmail].filter(Boolean).join(' · ') : '—'],
        ]}
      />
      <ReviewBlock
        title="Additional"
        onEdit={() => setTab('additional')}
        rows={[
          ['Sibling at school', siblings.hasSiblingAtSchool ? 'Yes' : 'No'],
          ...(siblings.hasSiblingAtSchool
            ? ([
                ['Sibling 1', `${siblings.sibling1.names} ${siblings.sibling1.surname}`.trim() || '—'],
                ['Sibling 2', `${siblings.sibling2.names} ${siblings.sibling2.surname}`.trim() || '—'],
                ['Sibling parent', [siblings.siblingParentNames, siblings.siblingParentEmail].filter(Boolean).join(' · ') || '—'],
              ] as [string, string][])
            : []),
          ['Notes', personal.notes || '—'],
        ]}
      />

      {primary?.email && (
        <p className="text-xs text-gray-500 text-center mt-2">
          A confirmation email will be sent to <strong>{primary.email}</strong>.
        </p>
      )}
    </div>
  );
}

function ReviewBlock({
  title,
  rows,
  onEdit,
}: {
  title: string;
  rows: [string, string][];
  onEdit: () => void;
}) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        <button
          type="button"
          onClick={onEdit}
          className="text-xs text-[#2d5295] hover:underline"
        >
          Edit
        </button>
      </div>
      <dl className="divide-y divide-gray-100">
        {rows.map(([label, value], i) => (
          <div key={i} className="grid grid-cols-3 gap-3 px-4 py-2 text-sm">
            <dt className="text-gray-500">{label}</dt>
            <dd className="col-span-2 text-gray-900">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
